import { useEffect, useState } from "react";
import { coverUrl } from "@/lib/cover";

/**
 * Samples the average color of a cover image via a tiny offscreen canvas,
 * boosts it toward something usable as a gradient wash (dark averages read
 * as muddy — push saturation up and clamp lightness into a usable band).
 *
 * Cached in-memory by URL so navigating back to an already-seen playlist/
 * album/artist doesn't re-decode the image.
 *
 * Needs the image served with permissive CORS (our CF Worker /img/* proxy
 * sets Access-Control-Allow-Origin: * for every allowlisted CDN — see
 * lib/cover.ts). Anything else silently resolves to null and the caller
 * falls back to a flat background — this is best-effort polish, never a
 * hard requirement for the page to render correctly.
 */
const cache = new Map<string, string | null>();

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s, l];
}

function sampleAverageColor(img: HTMLImageElement): string | null {
  const canvas = document.createElement("canvas");
  const size = 24;
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, size, size);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, size, size).data;
  } catch {
    return null; // canvas tainted — cross-origin image without CORS headers
  }
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 200) continue; // skip near-transparent pixels
    r += data[i]; g += data[i + 1]; b += data[i + 2];
    n++;
  }
  if (n === 0) return null;
  r = Math.round(r / n); g = Math.round(g / n); b = Math.round(b / n);
  const [h, s] = rgbToHsl(r, g, b);
  // Push toward a punchy, mid-dark wash regardless of how flat the source
  // average was — a plain photo cover averages to mud otherwise.
  const sat = Math.max(s, 0.55);
  const light = 0.32;
  return `hsl(${h.toFixed(1)} ${(sat * 100).toFixed(0)}% ${(light * 100).toFixed(0)}%)`;
}

export function useCoverColor(coverId: string | null | undefined): string | null {
  const [color, setColor] = useState<string | null>(null);
  const url = coverId ? coverUrl(coverId, 300) : "";

  useEffect(() => {
    setColor(null);
    if (!url) return;
    if (cache.has(url)) { setColor(cache.get(url)!); return; }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      const c = sampleAverageColor(img);
      cache.set(url, c);
      setColor(c);
    };
    img.onerror = () => { if (!cancelled) { cache.set(url, null); setColor(null); } };
    img.src = url;
    return () => { cancelled = true; };
  }, [url]);

  return color;
}
